import {
    COLUMN_FORMAT,
    DISPATCH_STATUS_OPTIONS,
    FB_STATUS_OPTIONS,
    KEY_FIELD,
    ORDER_TYPE_OPTIONS,
    PAY_BY_OPTIONS,
    POD_STATUS_OPTIONS,
    TRIP_TYPE_OPTIONS,
    YES_NO_OPTIONS,
} from "../constants";

// DATE / DATETIME / INT / DEC / MONEY are the board-wide display types - see COLUMN_FORMAT in
// ../constants for where each one comes from in the legacy persistent fields.
const { DATE, DATETIME, INT, DEC, MONEY } = COLUMN_FORMAT;

/**
 * The FMDISPATCHOBJECT column dictionary, shared by every grid on the board that binds
 * DispatchObjectBoardDTO.
 *
 * Seven grids bind that DTO: the three top tabs (Pick, Del, Current) and four bottom ones (Pick,
 * Del, Empty Container, Hist Ship). They differ only in which columns they lead with - the Delphi
 * gives each its own default order - so the column set is declared once here and each grid supplies
 * its leading list (Architecture.md R1).
 *
 * This used to live in bottom/columns.js and serve the bottom four only, while the top three carried
 * a hand-written <Column> block. That block was a subset: ninety columns the bottom tabs offered were
 * unreachable from a top tab's column chooser, and three the top tabs carried (ShipperZip, DriverName,
 * DeliveryComment) were missing from the bottom four. Moving the dictionary here and pointing all
 * seven grids at it is what closes both gaps, and is what keeps them closed.
 *
 * `caption` is the field's DisplayLabel from the matching data module, verbatim. Where the Delphi
 * defines none - the joined identity columns - the field name is the caption, which is what its grid
 * shows.
 */
const dispatchObjectColumn = {
    RecordNumber: { dataField: "RecordNumber", caption: "Record #" },
    TrackingNumber: { dataField: "TrackingNumber", caption: "Tracking #" },
    ContainerNumber: { dataField: "ContainerNumber", caption: "Container #" },
    Status: { dataField: "Status", caption: "Status", lookup: DISPATCH_STATUS_OPTIONS },
    ItemsOrdered: { dataField: "ItemsOrdered", caption: "Items", ...INT },
    WeightOrdered: { dataField: "WeightOrdered", caption: "Weight", ...DEC },
    CubesOrdered: { dataField: "CubesOrdered", caption: "Cubes", ...DEC },
    PalletsOrdered: { dataField: "PalletsOrdered", caption: "Pallets", ...INT },
    CommodityCode: { dataField: "CommodityCode", caption: "Commodity" },
    Origin: { dataField: "Origin", caption: "Origin" },
    Destination: { dataField: "Destination", caption: "Destination" },
    PayBy: { dataField: "PayBy", caption: "Pay By", lookup: PAY_BY_OPTIONS },
    ReferencePoNumber: { dataField: "ReferencePoNumber", caption: "Ref/PO#" },
    BillOfLadingNumber: { dataField: "BillOfLadingNumber", caption: "Bill of Lading" },
    ObjectType: { dataField: "ObjectType", caption: "Object Type" },
    DispatchGroup: { dataField: "DispatchGroup", caption: "Group" },
    CallInDate: { dataField: "CallInDate", caption: "Call in Date", ...DATE },
    Equipment: { dataField: "Equipment", caption: "Equipment" },
    Mileage: { dataField: "Mileage", caption: "Mileage", ...DEC },
    ActualPickupDt: { dataField: "ActualPickupDt", caption: "Actual P/U", ...DATETIME },
    ActualDeliveryDt: { dataField: "ActualDeliveryDt", caption: "Actual Delivery", ...DATETIME },
    ScheduledPuDepartureDt: { dataField: "ScheduledPuDepartureDt", caption: "PU Depart D/T", ...DATETIME },
    ScheduledDelDepartureDt: { dataField: "ScheduledDelDepartureDt", caption: "Del Depart D/T", ...DATETIME },
    ActualPuDepartureDt: { dataField: "ActualPuDepartureDt", caption: "Actual PU Depart", ...DATETIME },
    ActualDelDepartureDt: { dataField: "ActualDelDepartureDt", caption: "Actual Del Depart", ...DATETIME },
    ScheduledPickupDt: { dataField: "ScheduledPickupDt", caption: "Scheduled Pickup", ...DATETIME },
    ScheduledDeliveryDt: { dataField: "ScheduledDeliveryDt", caption: "Scheduled Delivery", ...DATETIME },
    ItemsActual: { dataField: "ItemsActual", caption: "Actual Item", ...INT },
    WeightActual: { dataField: "WeightActual", caption: "Actual Weight", ...DEC },
    CubesActual: { dataField: "CubesActual", caption: "Actual Cubes", ...DEC },
    PalletsActual: { dataField: "PalletsActual", caption: "Actual Pallets", ...INT },
    CreateTripSegments: { dataField: "CreateTripSegments", caption: "Create Trip Seg", lookup: YES_NO_OPTIONS },
    ShipperId: { dataField: "ShipperId", caption: "ShipperID" },
    ConsigneeId: { dataField: "ConsigneeId", caption: "ConsigneeID" },
    CustomerId: { dataField: "CustomerId", caption: "CustomerID" },

    // ---- the remaining FMDISPATCHOBJECT columns the legacy bottom grids carry. They reach every
    // tab through dispatchObjectRest as chooser entries, so no default column set changes.
    //
    // ACTUALDELDEPATUREDT is not one of them: it is the same column as ActualDelDepartureDt above,
    // and a second entry for it put two chooser rows with the identical caption "Actual Del Depart"
    // in every bottom dispatch-object grid. Same for PICKUPROUTE / DoPickRoute further down.
    USERDEFINEDCODE_1: { dataField: "UserDefinedCode1", caption: "Code 1" },
    USERDEFINEDCODE_2: { dataField: "UserDefinedCode2", caption: "Code 2" },
    USERDEFINEDCODE_3: { dataField: "UserDefinedCode3", caption: "Code 3" },
    USERDEFINEDCODE_4: { dataField: "UserDefinedCode4", caption: "Code 4" },
    USERDEFINEDCODE_5: { dataField: "UserDefinedCode5", caption: "Code 5" },
    USERDEFINEDREFERENCE_1: { dataField: "UserDefinedReference1", caption: "Reference 1" },
    USERDEFINEDREFERENCE_2: { dataField: "UserDefinedReference2", caption: "Reference 2" },
    USERDEFINEDREFERENCE_3: { dataField: "UserDefinedReference3", caption: "Reference 3" },
    USERDEFINEDREFERENCE_4: { dataField: "UserDefinedReference4", caption: "Reference 4" },
    USERDEFINEDREFERENCE_5: { dataField: "UserDefinedReference5", caption: "Reference 5" },
    USERDEFINEDDATE_1: { dataField: "UserDefinedDate1", caption: "Date 1", ...DATE },
    USERDEFINEDDATE_2: { dataField: "UserDefinedDate2", caption: "Date 2", ...DATE },
    USERDEFINEDDATE_3: { dataField: "UserDefinedDate3", caption: "Date 3", ...DATE },
    USERDEFINEDDATE_4: { dataField: "UserDefinedDate4", caption: "Date 4", ...DATE },
    USERDEFINEDDATE_5: { dataField: "UserDefinedDate5", caption: "Date 5", ...DATE },
    USERDEFINEDQUANTITY_1: { dataField: "UserDefinedQuantity1", caption: "Quantity 1", ...DEC },
    USERDEFINEDQUANTITY_2: { dataField: "UserDefinedQuantity2", caption: "Quantity 2", ...DEC },
    USERDEFINEDQUANTITY_3: { dataField: "UserDefinedQuantity3", caption: "Quantity 3", ...DEC },
    USERDEFINEDQUANTITY_4: { dataField: "UserDefinedQuantity4", caption: "Quantity 4", ...DEC },
    USERDEFINEDQUANTITY_5: { dataField: "UserDefinedQuantity5", caption: "Quantity 5", ...DEC },
    USERDEFINEDCODE_6: { dataField: "UserDefinedCode6", caption: "Code 6" },
    USERDEFINEDCODE_7: { dataField: "UserDefinedCode7", caption: "Code 7" },
    USERDEFINEDCODE_8: { dataField: "UserDefinedCode8", caption: "Code 8" },
    USERDEFINEDCODE_9: { dataField: "UserDefinedCode9", caption: "Code 9" },
    USERDEFINEDCODE_10: { dataField: "UserDefinedCode10", caption: "Code 10" },
    USERDEFINEDREFERENCE_6: { dataField: "UserDefinedReference6", caption: "Reference 6" },
    USERDEFINEDREFERENCE_7: { dataField: "UserDefinedReference7", caption: "Reference 7" },
    USERDEFINEDREFERENCE_8: { dataField: "UserDefinedReference8", caption: "Reference 8" },
    USERDEFINEDREFERENCE_9: { dataField: "UserDefinedReference9", caption: "Reference 9" },
    USERDEFINEDREFERENCE_10: { dataField: "UserDefinedReference10", caption: "Reference 10" },
    USERDEFINEDDATE_6: { dataField: "UserDefinedDate6", caption: "Date 6", ...DATE },
    USERDEFINEDDATE_7: { dataField: "UserDefinedDate7", caption: "Date 7", ...DATE },
    USERDEFINEDDATE_8: { dataField: "UserDefinedDate8", caption: "Date 8", ...DATE },
    USERDEFINEDDATE_9: { dataField: "UserDefinedDate9", caption: "Date 9", ...DATE },
    USERDEFINEDDATE_10: { dataField: "UserDefinedDate10", caption: "Date 10", ...DATE },
    USERDEFINEDQUANTITY_6: { dataField: "UserDefinedQuantity6", caption: "Quantity 6", ...DEC },
    USERDEFINEDQUANTITY_7: { dataField: "UserDefinedQuantity7", caption: "Quantity 7", ...DEC },
    USERDEFINEDQUANTITY_8: { dataField: "UserDefinedQuantity8", caption: "Quantity 8", ...DEC },
    USERDEFINEDQUANTITY_9: { dataField: "UserDefinedQuantity9", caption: "Quantity 9", ...DEC },
    USERDEFINEDQUANTITY_10: { dataField: "UserDefinedQuantity10", caption: "Quantity 10", ...DEC },
    SOURCETABLENAME: { dataField: "SourceTableName", caption: "Src Table Name" },
    PUAPPOINTMENTDT: { dataField: "PuAppointmentDt", caption: "PU Appointment D/T", ...DATETIME },
    PUAPPOINTMENTDOCK: { dataField: "PuAppointmentDock", caption: "PU Appointment Dock" },
    PUAPPOINTMENTREFERENCE: { dataField: "PuAppointmentReference", caption: "PU Appointment Ref" },
    DELAPPOINTMENTDT: { dataField: "DelAppointmentDt", caption: "Del Appointment D/T", ...DATETIME },
    DELAPPOINTMENTDOCK: { dataField: "DelAppointmentDock", caption: "Del Appointment Dock" },
    DELAPPOINTMENTREFERENCE: { dataField: "DelAppointmentReference", caption: "Del Appointment Ref" },
    CONTAINERSIZEQUALIFIER: { dataField: "ContainerSizeQualifier", caption: "Container Size Qualifier" },
    CHASSISNUMBER: { dataField: "ChassisNumber", caption: "Chassis #" },
    EIRREFERENCE: { dataField: "EirReference", caption: "EIR/Reference" },
    PRENOTEDATE: { dataField: "PrenoteDate", caption: "Pre Note Date", ...DATE },
    PRENOTEREFERENCE: { dataField: "PrenoteReference", caption: "Pre Note Ref" },
    VESSEL: { dataField: "Vessel", caption: "Vessel" },
    BOOKINGNUMBER: { dataField: "BookingNumber", caption: "Booking #" },
    EXPECTEDARRIVALDATE: { dataField: "ExpectedArrivalDate", caption: "Expected Arrival Date", ...DATE },
    HOLDS: { dataField: "Holds", caption: "Holds" },
    HOLDSRELEASEDATE: { dataField: "HoldsReleaseDate", caption: "Release Date", ...DATE },
    NOTIFICATIONDATE: { dataField: "NotificationDate", caption: "Notification Date", ...DATE },
    NOTIFICATIONREFERENCE: { dataField: "NotificationReference", caption: "Notification Ref" },
    NOTIFICATIONARRIVALDATE: { dataField: "NotificationArrivalDate", caption: "Arrival Date", ...DATE },
    PULASTFREEDATE: { dataField: "PuLastFreeDate", caption: "PU Last Free Date", ...DATE },
    DEMURRAGEAMOUNT: { dataField: "DemurrageAmount", caption: "Demurrage Amt", ...MONEY },
    DELIVERYPLACEMENT: { dataField: "DeliveryPlacement", caption: "Placement Date", ...DATETIME },
    DELIVERYPICKUP: { dataField: "DeliveryPickup", caption: "Pickup Date", ...DATETIME },
    RETURNLASTFREEDATE: { dataField: "ReturnLastFreeDate", caption: "Return Last Free Date", ...DATE },
    DETENTIONAMOUNT: { dataField: "DetentionAmount", caption: "Detention Amt", ...MONEY },
    TRUCKREFERENCE: { dataField: "TruckReference", caption: "Truck Ref" },
    DRIVERREFERENCE: { dataField: "DriverReference", caption: "Driver Ref" },
    TRAILERREFERENCE: { dataField: "TrailerReference", caption: "Trailer Ref" },
    TRIPTYPE2: { dataField: "TripType2", caption: "Trip Type 2", lookup: TRIP_TYPE_OPTIONS },
    TRIPREQUIRED_1: { dataField: "TripRequired1", caption: "Trip Req 1" },
    TRIPREQUIRED_2: { dataField: "TripRequired2", caption: "Trip Req 2" },
    TRIPREQUIRED_3: { dataField: "TripRequired3", caption: "Trip Req 3" },
    TSMILES_1: { dataField: "TsMiles1", caption: "Trip Seg Mile", ...DEC },
    TSMILES_2: { dataField: "TsMiles2", caption: "Trip Seg Mile 2", ...DEC },
    TSMILES_3: { dataField: "TsMiles3", caption: "Trip Seg Mile 3", ...DEC },
    LINEHAUL: { dataField: "Linehaul", caption: "Linehaul" },
    OSDCODE: { dataField: "OsdCode", caption: "OSD Code" },
    TERMINAL1TSFAX: { dataField: "Terminal1TsFax", caption: "Terminal Fax 1" },
    FOLLOWUPREFERENCE: { dataField: "FollowupReference", caption: "Follow up Ref" },
    TERMINAL2TSFAX: { dataField: "Terminal2TsFax", caption: "Terminal Fax 2" },
    FBSTATUS: { dataField: "FbStatus", caption: "FB Status", lookup: FB_STATUS_OPTIONS },
    PODSTATUS: { dataField: "PodStatus", caption: "POD Status", lookup: POD_STATUS_OPTIONS },

    // ---- carried by the DTO, added from the legacy grids (uFMM0056 / uFMM0061 / uFMM0027 /
    // uFMM0046). All reach the tabs through dispatchObjectRest as chooser entries, so no tab's
    // default column set changes. Captions follow the top board grid where it names the same
    // property, so one field is not "Truck" above and "TruckId" below.
    OrderType: { dataField: "OrderType", caption: "Order Type", lookup: ORDER_TYPE_OPTIONS },
    DispatcherId: { dataField: "DispatcherId", caption: "Dispatcher" },
    TruckId: { dataField: "TruckId", caption: "Truck" },
    TrailerId: { dataField: "TrailerId", caption: "Trailer" },
    DriverId: { dataField: "DriverId", caption: "Driver" },
    ShipperName: { dataField: "ShipperName", caption: "ShipperName" },
    ShipperCity: { dataField: "ShipperCity", caption: "ShipperCity" },
    ShipperState: { dataField: "ShipperState", caption: "ShipperState" },
    ConsigneeName: { dataField: "ConsigneeName", caption: "ConsigneeName" },
    ConsigneeCity: { dataField: "ConsigneeCity", caption: "ConsigneeCity" },
    ConsigneeState: { dataField: "ConsigneeState", caption: "ConsigneeState" },
    ConsigneeZip: { dataField: "ConsigneeZip", caption: "ConsigneeZip" },
    CustomerName: { dataField: "CustomerName", caption: "CustomerName" },
    CustomerZip: { dataField: "CustomerZip", caption: "CustomerZip" },
    CustomerState: { dataField: "CustomerState", caption: "CustomerState" },
    CustomerCity: { dataField: "CustomerCity", caption: "CustomerCity" },
    Stage: { dataField: "Stage", caption: "Stage" },
    LastUpdateUserId: { dataField: "LastUpdateUserId", caption: "Last Updated By" },
    LastUpdateDateTime: { dataField: "LastUpdateDateTime", caption: "Last Updated", ...DATE },
    CreationUserId: { dataField: "CreationUserId", caption: "Created By" },
    CreationDateTime: { dataField: "CreationDateTime", caption: "Creation Date", ...DATE },
    ContainerReturn: { dataField: "ContainerReturn", caption: "Container Return", lookup: YES_NO_OPTIONS },
    DispatchingMethod: { dataField: "DispatchingMethod", caption: "Dispatching Method" },
    CarrierId: { dataField: "CarrierId", caption: "Carrier ID" },
    TripType: { dataField: "TripType", caption: "Trip Type", lookup: TRIP_TYPE_OPTIONS },
    PickupRoute: { dataField: "PickupRoute", caption: "Pickup Route" },
    DeliveryRoute: { dataField: "DeliveryRoute", caption: "Delivery Route" },
    CarrierName: { dataField: "CarrierName", caption: "CarrierName" },
    OrderReferenceNumber: { dataField: "OrderReferenceNumber", caption: "Order Reference" },
    OrderBillOfLading: { dataField: "OrderBillOfLading", caption: "Order Bill of Lading" },
    OrderByReference: { dataField: "OrderByReference", caption: "Order By Reference" },
    FreightClassCode: { dataField: "FreightClassCode", caption: "Freight Class" },
    PickupComment: { dataField: "PickupComment", caption: "PU Comment" },
    Comment: { dataField: "Comment", caption: "Comment" },
    PickupRouteDescription: { dataField: "PickupRouteDescription", caption: "Pickup Route Description" },
    DeliveryRouteDescription: { dataField: "DeliveryRouteDescription", caption: "Delivery Route Description" },
    ProfileCode: { dataField: "ProfileCode", caption: "Profile Code" },
    OrderNumber: { dataField: "OrderNumber", caption: "Order #" },
    OmTotalFreight: { dataField: "OmTotalFreight", caption: "OM Freight", ...DEC },
    OmTotalMerchandise: { dataField: "OmTotalMerchandise", caption: "OM Merc", ...DEC },
    OriginWeight: { dataField: "OriginWeight", caption: "Origin Weight", ...DEC },
    DestinationWeight: { dataField: "DestinationWeight", caption: "Destination Weight", ...DEC },
    CompanyId: { dataField: "CompanyId", caption: "CompanyID" },
    Module: { dataField: "Module", caption: "Module" },
    RecordType: { dataField: "RecordType", caption: "Record Type" },

    // ---- FMDISPATCHOBJECT columns the entity carries and the API had dropped -------------------
    //
    // The seven integers are TAbIntegerField in the Delphi, and that class omits VisibleOnGrid from
    // the DFM when it is true - which is why they read as absent from the legacy grids. The three
    // amounts are TAbCurrencyField, hence MONEY rather than DEC; only TSMILES_1..3 had been carried.
    TSAMOUNT_1: { dataField: "TsAmount1", caption: "Trip Seg Amt", ...MONEY },
    TSAMOUNT_2: { dataField: "TsAmount2", caption: "Trip Seg Amt 2", ...MONEY },
    TSAMOUNT_3: { dataField: "TsAmount3", caption: "Trip Seg Amt 3", ...MONEY },
    OSDCOMMENT: { dataField: "OsdComment", caption: "OSD Comment" },
    USERDEFINEDCOMMENT: { dataField: "UserDefinedComment", caption: "User Defined Comment" },
    CREATETRIP: { dataField: "CreateTrip", caption: "Create Trip" },
    CONTAINERSIZE: { dataField: "ContainerSize", caption: "Container", ...INT },
    PICKUPNO: { dataField: "PickupNo", caption: "PU #", ...INT },
    DELIVERYNO: { dataField: "DeliveryNo", caption: "Del #", ...INT },
    DEMURRAGEDAYS: { dataField: "DemurrageDays", caption: "Demurrage Days", ...INT },
    DETENTIONDAYS: { dataField: "DetentionDays", caption: "Detention Days", ...INT },
    FREEPUDAYS: { dataField: "FreePuDays", caption: "Free PU Days", ...INT },
    FREERTNDAYS: { dataField: "FreeRtnDays", caption: "Free Rtn Days", ...INT },

    // ---- ORDERDATETIME ------------------------------------------------------------------------
    // In the DTO and on no grid until now. The API computes it specially - the Delphi shows it only
    // for master orders, and only the Pick query selects it at all - so on Del and Current it is
    // blank by design rather than by omission.
    ORDERDATETIME: { dataField: "OrderDateTime", caption: "Order D/T", ...DATETIME },

    // ---- the identity views' address and contact columns ---------------------------------------
    // No DisplayLabel in uFMM0036, so the field name is the caption - which is what the desktop
    // grid shows, and matches CustomerName / ShipperCity / ConsigneeState above.
    CustomerAddress1: { dataField: "CustomerAddress1", caption: "CustomerAddress1" },
    CustomerAddress2: { dataField: "CustomerAddress2", caption: "CustomerAddress2" },
    CustomerCountry: { dataField: "CustomerCountry", caption: "CustomerCountry" },
    CustomerTelephone: { dataField: "CustomerTelephone", caption: "CustomerTelephone" },
    CustomerFax: { dataField: "CustomerFax", caption: "CustomerFax" },
    ShipperAddress1: { dataField: "ShipperAddress1", caption: "ShipperAddress1" },
    ShipperAddress2: { dataField: "ShipperAddress2", caption: "ShipperAddress2" },
    ShipperCountry: { dataField: "ShipperCountry", caption: "ShipperCountry" },
    ShipperTelephone: { dataField: "ShipperTelephone", caption: "ShipperTelephone" },
    ShipperFax: { dataField: "ShipperFax", caption: "ShipperFax" },
    ConsigneeAddress1: { dataField: "ConsigneeAddress1", caption: "ConsigneeAddress1" },
    ConsigneeAddress2: { dataField: "ConsigneeAddress2", caption: "ConsigneeAddress2" },
    ConsigneeCountry: { dataField: "ConsigneeCountry", caption: "ConsigneeCountry" },
    ConsigneeTelephone: { dataField: "ConsigneeTelephone", caption: "ConsigneeTelephone" },
    ConsigneeFax: { dataField: "ConsigneeFax", caption: "ConsigneeFax" },

    // ---- the agent ------------------------------------------------------------------------------
    // The fourth identity view, and for a while the only part of the legacy FieldList the board did
    // not carry: vw_IDAgentView was not in the EF model, so the API dropped both columns and every
    // chooser on the board was two entries short of the desktop grid. It is mapped now (see
    // VwIdagentview), and these sit where the legacy SELECT puts them - after the consignee's
    // contact columns and before the carrier's. Captions are uFMM0036's DisplayLabels: "Agent" for
    // the id, not "AgentID", and "Agent Name".
    AgentId: { dataField: "AgentId", caption: "Agent" },
    AgentName: { dataField: "AgentName", caption: "Agent Name" },

    // ---- carrier contact, and the second and third carrier and trailer slots -------------------
    CarrierPhone: { dataField: "CarrierPhone", caption: "CarrierPhone" },
    CarrierFax: { dataField: "CarrierFax", caption: "CarrierFax" },
    Carrier2: { dataField: "Carrier2", caption: "Carrier2" },
    Carrier3: { dataField: "Carrier3", caption: "Carrier3" },
    Trailer2Id: { dataField: "Trailer2Id", caption: "Trailer2ID" },
    Trailer3Id: { dataField: "Trailer3Id", caption: "Trailer3ID" },

    // ---- the columns the top grids carried and the shared dictionary did not --------------------
    // Present on the hand-written top grid before it was replaced by this dictionary, and therefore
    // missing from bottom Pick, bottom Del, bottom Empty Container and bottom Hist Ship.
    ShipperZip: { dataField: "ShipperZip", caption: "ShipperZip" },
    DriverName: { dataField: "DriverName", caption: "Driver Name" },
    DeliveryComment: { dataField: "DeliveryComment", caption: "Del Comment" },

    // ---- FMORDERS' ten-each user-defined columns ------------------------------------------------
    // Aliased OrdersUDC_n / UDD_n / UDQ_n / UDR_n by the legacy query so they do not collide with
    // FMDISPATCHOBJECT's identically-named set above, and captioned as it labels them.
    ORDERSUDC_1: { dataField: "OrdersUDC_1", caption: "Orders UDC 1" },
    ORDERSUDC_2: { dataField: "OrdersUDC_2", caption: "Orders UDC 2" },
    ORDERSUDC_3: { dataField: "OrdersUDC_3", caption: "Orders UDC 3" },
    ORDERSUDC_4: { dataField: "OrdersUDC_4", caption: "Orders UDC 4" },
    ORDERSUDC_5: { dataField: "OrdersUDC_5", caption: "Orders UDC 5" },
    ORDERSUDC_6: { dataField: "OrdersUDC_6", caption: "Orders UDC 6" },
    ORDERSUDC_7: { dataField: "OrdersUDC_7", caption: "Orders UDC 7" },
    ORDERSUDC_8: { dataField: "OrdersUDC_8", caption: "Orders UDC 8" },
    ORDERSUDC_9: { dataField: "OrdersUDC_9", caption: "Orders UDC 9" },
    ORDERSUDC_10: { dataField: "OrdersUDC_10", caption: "Orders UDC 10" },
    ORDERSUDR_1: { dataField: "OrdersUDR_1", caption: "Orders UDR 1" },
    ORDERSUDR_2: { dataField: "OrdersUDR_2", caption: "Orders UDR 2" },
    ORDERSUDR_3: { dataField: "OrdersUDR_3", caption: "Orders UDR 3" },
    ORDERSUDR_4: { dataField: "OrdersUDR_4", caption: "Orders UDR 4" },
    ORDERSUDR_5: { dataField: "OrdersUDR_5", caption: "Orders UDR 5" },
    ORDERSUDR_6: { dataField: "OrdersUDR_6", caption: "Orders UDR 6" },
    ORDERSUDR_7: { dataField: "OrdersUDR_7", caption: "Orders UDR 7" },
    ORDERSUDR_8: { dataField: "OrdersUDR_8", caption: "Orders UDR 8" },
    ORDERSUDR_9: { dataField: "OrdersUDR_9", caption: "Orders UDR 9" },
    ORDERSUDR_10: { dataField: "OrdersUDR_10", caption: "Orders UDR 10" },
    ORDERSUDD_1: { dataField: "OrdersUDD_1", caption: "Orders UDD 1", ...DATE },
    ORDERSUDD_2: { dataField: "OrdersUDD_2", caption: "Orders UDD 2", ...DATE },
    ORDERSUDD_3: { dataField: "OrdersUDD_3", caption: "Orders UDD 3", ...DATE },
    ORDERSUDD_4: { dataField: "OrdersUDD_4", caption: "Orders UDD 4", ...DATE },
    ORDERSUDD_5: { dataField: "OrdersUDD_5", caption: "Orders UDD 5", ...DATE },
    ORDERSUDD_6: { dataField: "OrdersUDD_6", caption: "Orders UDD 6", ...DATE },
    ORDERSUDD_7: { dataField: "OrdersUDD_7", caption: "Orders UDD 7", ...DATE },
    ORDERSUDD_8: { dataField: "OrdersUDD_8", caption: "Orders UDD 8", ...DATE },
    ORDERSUDD_9: { dataField: "OrdersUDD_9", caption: "Orders UDD 9", ...DATE },
    ORDERSUDD_10: { dataField: "OrdersUDD_10", caption: "Orders UDD 10", ...DATE },
    ORDERSUDQ_1: { dataField: "OrdersUDQ_1", caption: "Orders UDQ 1", ...DEC },
    ORDERSUDQ_2: { dataField: "OrdersUDQ_2", caption: "Orders UDQ 2", ...DEC },
    ORDERSUDQ_3: { dataField: "OrdersUDQ_3", caption: "Orders UDQ 3", ...DEC },
    ORDERSUDQ_4: { dataField: "OrdersUDQ_4", caption: "Orders UDQ 4", ...DEC },
    ORDERSUDQ_5: { dataField: "OrdersUDQ_5", caption: "Orders UDQ 5", ...DEC },
    ORDERSUDQ_6: { dataField: "OrdersUDQ_6", caption: "Orders UDQ 6", ...DEC },
    ORDERSUDQ_7: { dataField: "OrdersUDQ_7", caption: "Orders UDQ 7", ...DEC },
    ORDERSUDQ_8: { dataField: "OrdersUDQ_8", caption: "Orders UDQ 8", ...DEC },
    ORDERSUDQ_9: { dataField: "OrdersUDQ_9", caption: "Orders UDQ 9", ...DEC },
    ORDERSUDQ_10: { dataField: "OrdersUDQ_10", caption: "Orders UDQ 10", ...DEC },
};
const DISPATCH_OBJECT_KEYS = [
    { dataField: KEY_FIELD.ROW_ID, hidden: true },
    { dataField: "OrderRowId", hidden: true },
    { dataField: "TripHeaderRowId", hidden: true },
];

/** Everything on the DTO that this tab does not lead with, offered from the chooser. */
const dispatchObjectRest = (leading) => {
    const shown = new Set(leading.map((c) => c.dataField));
    return Object.values(dispatchObjectColumn)
        .filter((c) => !shown.has(c.dataField))
        .map((c) => ({ ...c, visible: false }));
};

/**
 * One tab's column set: its leading columns, then the keys, then everything else as chooser entries.
 *
 * Takes the column definitions themselves rather than their names. Naming them as strings meant every
 * field appeared twice - once declared in the dictionary above and once quoted here - and a typo in
 * the second copy resolved to `undefined`, which only shows up as a crash inside dispatchObjectRest.
 */
const dispatchObjectColumns = (leading) => [
    ...DISPATCH_OBJECT_KEYS,
    ...leading,
    ...dispatchObjectRest(leading),
];

export { dispatchObjectColumns };

/** Short alias, so a leading-column list reads as a list rather than as repeated dictionary lookups. */
export const col = dispatchObjectColumn;

/**
 * The Pick, Del and Current grids' default column set.
 *
 * The leading list is the order the DEV-6402 screenshot shows; everything else on the DTO follows as
 * a chooser entry. TripStatus is not here - only the Current query selects it, so DispatchBoardGrid
 * appends it for that tab alone rather than offering three tabs a column two of them leave blank.
 */
export const TOP_BOARD_COLUMNS = dispatchObjectColumns([
    col.RecordNumber, col.Destination, col.ShipperZip, col.TrackingNumber, col.CustomerId,
    col.ContainerNumber, col.CallInDate, col.Mileage, col.ScheduledPickupDt, col.ShipperId,
    col.ShipperCity, col.ConsigneeId, col.ConsigneeCity, col.ConsigneeZip,
]);
